import { HttpStatusCode } from 'axios';
import { useEffect, useState } from 'react';
import {
  DocumentFetchPresignedUrlsRequest,
  DocumentUploadPresignedUrlsRequest,
  Entity,
  PresignedUrlObject,
  VendorDocumentSubType,
} from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { getFileExtension } from '../../../../../../utils/FileUtils';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
import EditAccountInfoModal from './EditAccountInfoModal';
import EditBusinessInfoModal from './EditBusinessInfoModal';
import AccountInformation from './AccountInformation';
import BusinessInformation from './BusinessInformation';

interface ProfileOverviewProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function ProfileOverview({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: ProfileOverviewProps) {
  const { screenView } = useScreenView();
  const { userRecord, userId } = useUser();
  const [hover, setHover] = useState(false);
  const { callProtectedApi, callApi } = useApi();
  const [loadingImage, setLoadingImage] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [accountEditPopupVisible, setAccountEditPopupVisible] = useState(false);
  const [businessEditPopupVisible, setBusinessEditPopupVisible] =
    useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);
  const handleReplaceClick = () => {
    document.getElementById('fileInput')?.click();
  };

  const handleDeleteClick = () => {
    // Logic to handle deleting the image
    console.log('Delete image clicked');
  };

  const fetchProfileImage = async (vendorId: string) => {
    try {
      setLoadingImage(true);
      const responseDocUrl = await callProtectedApi(
        `/documents/display/vendor/${vendorId}`,
        'post',
        {
          document_count: 1,
          document_sub_type: VendorDocumentSubType.Logo,
        } as DocumentFetchPresignedUrlsRequest
      );
      const url: string[] = responseDocUrl?.data?.data?.document_urls;
      setImageUrl(url?.length > 0 ? url[0] : '');
    } catch (error: any) {
      console.error('Error occurred fetching profile image: ', error);
    } finally {
      setLoadingImage(false);
    }
  };

  const uploadImage = async (
    vendorId: string,
    fileExtension: string,
    file: File
  ) => {
    try {
      setLoadingImage(true);
      const responseDocUrl = await callProtectedApi(
        `/documents/vendor/${vendorId}`,
        'post',
        {
          document_count: 1,
          document_sub_type: VendorDocumentSubType.Logo,
          file_extensions: [fileExtension],
        } as DocumentUploadPresignedUrlsRequest,
        'Error occurred while uploading your profile image.'
      );
      const url: PresignedUrlObject[] =
        responseDocUrl?.data?.data?.document_urls;
      callApi(
        '',
        'put',
        file,
        'Error occurred while uploading your profile image.',
        {
          'Content-Type': file?.type,
        },
        url[0]?.presignedUrl,
        5
      ).then((result) => {
        if (result?.status === HttpStatusCode.Ok) {
          fetchProfileImage(userId ?? '');
        } else {
          throw new Error(
            `Failed to upload ${file?.name ?? 'file'}. HTTP Error ` +
              result?.status
          );
        }
      });
    } catch (error: any) {
      console.error('Error occurred uploading profile image.');
    } finally {
      setLoadingImage(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && ['image/png', 'image/jpeg', 'image/heic'].includes(file.type)) {
      setUploadedFile(file);
      const fileExtension = getFileExtension(file);
      // Here, you would typically also upload the file to the server or cloud storage
      if (fileExtension && file) {
        if (userRecord?.userType === Entity.Vendor) {
          uploadImage(userId ?? '', fileExtension, file);
        }
      }
    } else {
      alert('Please upload a valid image file (png, jpeg, heic, heif).');
    }
  };

  useEffect(() => {
    if (userRecord?.userType === Entity.Vendor && imageUrl?.length === 0) {
      fetchProfileImage(userId ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className={`flex h-full w-full flex-col items-center gap-6 border-[1px] border-bordergray bg-white p-4 align-middle`}
          >
            {businessEditPopupVisible && (
              <EditBusinessInfoModal
                onClose={() => setBusinessEditPopupVisible(false)}
                isOpen={businessEditPopupVisible}
              />
            )}
            {accountEditPopupVisible && (
              <EditAccountInfoModal
                onClose={() => setAccountEditPopupVisible(false)}
                isOpen={accountEditPopupVisible}
              />
            )}
            {userRecord && userRecord?.userType === Entity.Vendor && (
              <div className="flex w-full flex-col items-start justify-between gap-4 align-middle lg:flex-row">
                <AccountInformation
                  imageUrl={imageUrl}
                  loadingImage={loadingImage}
                  hover={hover}
                  uploadedFile={uploadedFile}
                  handleMouseEnter={handleMouseEnter}
                  handleMouseLeave={handleMouseLeave}
                  handleDeleteClick={handleDeleteClick}
                  handleReplaceClick={handleReplaceClick}
                  handleFileChange={handleFileChange}
                  setAccountEditPopupVisible={setAccountEditPopupVisible}
                />
                <BusinessInformation
                  setBusinessEditPopupVisible={setBusinessEditPopupVisible}
                />
              </div>
            )}
          </div>
        );
    }
  };
  return (
    <>
      {userRecord &&
        userRecord?.userType === Entity.Vendor &&
        userRecord?.status === 'active' &&
        renderScreenView(screenView)}
    </>
  );
}

export default ProfileOverview;
